/**
 * RecoveryNudge — onb-001-3-fe ③ persistent lost-II recovery nudge.
 *
 * A portal-entry, recurring, dismissible reminder encouraging a linked member to
 * add a backup factor to their Internet Identity. Mounted in AppLayout (the
 * authenticated portal shell) so it recurs across portal pages.
 *
 * DELIBERATELY NON-BLOCKING (Menley's Q2 ruling): II enrollment cannot be verified
 * externally, so this NEVER gates completion. It is encouragement only — shown,
 * dismissible, and it comes back on a later session. Dismissal is per-session
 * (sessionStorage) so the nudge recurs without nagging within a single visit.
 *
 * SHIPS DARK: gated behind `isOnboardingGateEnabled()` (VITE_ONBOARDING_GATE_ENABLED).
 * Flag OFF ⇒ renders nothing (today's behavior exactly).
 *
 * Copy is RATIFIED (MENLEY-RESPONSE-2026-10-09 §for-Coby) — verbatim.
 *
 * Story: onb-001-3-fe
 */
import { useState } from 'react';
import { useAuth } from '@hello-world-dao/auth';
import { ShieldAlert, X } from 'lucide-react';
import { isOnboardingGateEnabled } from '@/utils/featureFlags';

/** II identity provider (mainnet) — the "Set it up" CTA opens this in a new window. */
const II_IDENTITY_PROVIDER = 'https://identity.ic0.app';

/** Per-session dismissal key. sessionStorage ⇒ the nudge recurs on a new session. */
const DISMISS_KEY = 'onb-recovery-nudge-dismissed';

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === 'true';
  } catch {
    // Private mode / blocked storage — treat as not dismissed (show the nudge). The
    // nudge is non-blocking, so over-showing is safe; under-showing hides a warning.
    return false;
  }
}

/**
 * Whether the `recovery_factor_enrolled` hint "looks like a single factor" (so the
 * member still needs a backup). The hint is not yet emitted by the session/JWT
 * (the backend field is future work, and this feature ships DARK), so it is read
 * DEFENSIVELY off the auth user: the nudge HIDES only on a positive multi-factor
 * signal; absent/ambiguous ⇒ show. Over-showing is safe (non-blocking + dismissible);
 * it NEVER gates, so an unverifiable hint never blocks a member.
 */
function looksLikeSingleFactor(user: unknown): boolean {
  if (user == null || typeof user !== 'object') return true;
  const u = user as Record<string, unknown>;
  const hint = u.recovery_factor_enrolled ?? u.recoveryFactorEnrolled;
  // A numeric factor count: hide only when 2+ factors are enrolled (protected).
  if (typeof hint === 'number') return hint < 2;
  // An explicit "multiple factors" array/signal: hide when it has 2+ entries.
  if (Array.isArray(hint)) return hint.length < 2;
  // Anything else (undefined, boolean, string) ⇒ cannot confirm multiple ⇒ show.
  return true;
}

export function RecoveryNudge() {
  const { icPrincipal, user } = useAuth();
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());

  // Hooks are all above this line (rules-of-hooks); visibility is decided after.
  if (!isOnboardingGateEnabled()) return null; // SHIPS DARK
  if (dismissed) return null;
  if (!icPrincipal) return null; // only a member who has crossed (linked II)
  if (!looksLikeSingleFactor(user)) return null; // already protected ⇒ no nudge

  function dismiss() {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      // Best-effort: state alone hides it for this mount even if storage is blocked.
    }
  }

  function handleSetItUp() {
    // Opens II in a new window — NON-blocking; the nudge stays (dismissal is the
    // only way it goes away this session) because enrollment cannot be verified.
    window.open(II_IDENTITY_PROVIDER, '_blank', 'noopener,noreferrer');
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="recovery-nudge"
      className="mx-4 mt-4 flex items-start gap-3 rounded-md border border-border bg-card p-3 text-sm shadow-sm"
    >
      <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
      <p className="flex-1">
        Finish protecting your membership: add a backup to your Internet Identity so
        you never lose access.{' '}
        <button
          type="button"
          onClick={handleSetItUp}
          data-testid="recovery-nudge-cta"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Set it up
        </button>
      </p>
      <button
        type="button"
        onClick={dismiss}
        data-testid="recovery-nudge-dismiss"
        aria-label="Dismiss"
        className="shrink-0 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export default RecoveryNudge;
